import type { ComponentType, ReactNode } from 'react';
import { Route } from 'react-router-dom';
import { Outlet } from 'react-router';
import type { AppRouteDefinition } from './types';

function wrapElement(
  element: ReactNode,
  Wrapper?: ComponentType<{ children: ReactNode }>,
): ReactNode {
  if (!Wrapper) return element;
  return <Wrapper>{element}</Wrapper>;
}

function renderRouteElement(route: AppRouteDefinition): ReactNode | undefined {
  const outletElement = <Outlet />;

  if (route.element) {
    return wrapElement(route.element, route.guard);
  }

  if (!route.component) {
    if (route.guard) {
      return wrapElement(outletElement, route.guard);
    }
    return undefined;
  }

  const Component = route.component;
  const componentElement = <Component />;
  return wrapElement(componentElement, route.guard);
}

export function renderRoutes(routes: AppRouteDefinition[]): ReactNode[] {
  return routes
    .filter((route) => (route.devOnly ? import.meta.env.DEV : true))
    .map((route) => {
      const children = route.children ? renderRoutes(route.children) : null;
      const element = renderRouteElement(route);

      return (
        <Route key={route.name} path={route.path} element={element}>
          {children}
        </Route>
      );
    });
}
